import { Cancel, PictureAsPdf, UploadFile } from '@mui/icons-material';
import { Button, Dialog, Divider, Grid, LinearProgress, TextField, Typography } from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';
import { styled } from '@mui/material/styles';
import MainCard from 'ui-component/cards/MainCard';
import AnimateButton from 'ui-component/extended/AnimateButton';
import { useState } from 'react';
import { uuidv4 } from '@firebase/util';
import { getCurrentDateTime } from 'utils/DateTime';
import FirebaseStorageService from 'firebaseService/firebaseStorage';
import Swal from 'sweetalert2';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

export const UploadFormPdfModal = ({ open, onClose, user, dependency, doUpdateDependencyHandler }) => {
  const theme = useTheme();
  const [form1, setForm1] = useState('');
  const [form2, setForm2] = useState('');
  const [isLoading, setLoading] = useState(false);

  const getRole = (role) => {
    let roleAsString = '';
    if (role === 'USER_ROLE') {
      roleAsString = 'Normal';
    } else if (role === 'ADMIN_ROLE') {
      roleAsString = 'Administrador';
    } else if (role === 'PM_ROLE') {
      roleAsString = 'Projec Manager';
    } else {
      roleAsString = 'Normal';
    }
    return roleAsString;
  };

  const handleFileSelect1 = (e) => {
    const file = e.target.files[0];
    if (file) setForm1(file);
  };

  const handleFileSelect2 = (e) => {
    const file = e.target.files[0];
    if (file) setForm2(file);
  };

  const handlerSubmit = async () => {
    try {
      setLoading(true);
      const filesToUpload = [];
      if (form1) {
        filesToUpload.push({ doc: form1, form: 'form1' });
      }

      if (form2) {
        filesToUpload.push({ doc: form2, form: 'form2' });
      }

      const filesPromises = filesToUpload.map(async (file) => {
        const path = `dependencies/${dependency.id}/${uuidv4()}`;
        const downloadURL = await FirebaseStorageService.uploadFile(path, file.doc);

        const fileObject = {
          name: file.form,
          type: file.doc.type,
          uploadedAt: getCurrentDateTime(),
          src: path,
          downloadURL: downloadURL
        };

        return fileObject;
      });

      const filesUploaded = await Promise.all(filesPromises);
      const response = doUpdateDependencyHandler(filesUploaded);
      if (response.success) {
        Swal.fire({
          title: '¡Documentos subidos correctamente!',
          text: 'Ambos formatos fueron cargados correctamente, en las proximas horas seran verficados y autorizados el equipo de Google LLC.',
          icon: 'success',
          timer: 2500,
          timerProgressBar: true,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadURL = async (path) => {
    try {
      setLoading(true);
      const url = await FirebaseStorageService.downloadFile(path);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={'md'} fullWidth>
        <MainCard
          title={'Subir formatos diligenciados'}
          secondary={
            <Grid container justifyContent={'center'}>
              <Box>
                <Cancel sx={{ fontSize: '40px' }} color={theme.palette.grey[500]} onClick={onClose} />
              </Box>
            </Grid>
          }
        >
          <div>
            <Typography variant="h2">Funcionario: {user.displayName}</Typography>
            <Typography variant="h4" sx={{ marginTop: '5px' }}>
              Rol: {getRole(JSON.parse(user?.reloadUserInfo?.customAttributes).role)}
            </Typography>
            <Typography variant="h5">Registro: {user.metadata.creationTime}</Typography>
          </div>
          <Divider sx={{ marginTop: '20px', marginBottom: '10px' }} />

          <Typography variant="h2">Dependencia: {dependency.dependence}</Typography>
          <Typography variant="h4" sx={{ marginTop: '5px' }}>
            Tipo: {dependency.type}
          </Typography>
          <Typography variant="h5">Sigla: {dependency.abbreviation}</Typography>
          {dependency.format_1 !== '' && dependency.format_2 !== '' && (
            <Typography variant="body1" sx={{ color: '#00C853' }} mt={1}>
              <strong>Nota:</strong>
              Esta institución ya tiene unos formatos de autorización, en las proximas horas un miembro del equipo de Google realizara la
              validación de los documentos.
            </Typography>
          )}
          <Divider sx={{ marginTop: '10px', marginBottom: '10px' }} />
          <Grid container alignItems="center" justifyContent="center">
            <Grid item container alignItems="center" justifyContent="center">
              <Grid item xs={12} sm={4}>
                <Button disabled={dependency.format_1 !== ''} variant="outlined" endIcon={<PictureAsPdf />} component="label">
                  Cargar formato 1
                  <VisuallyHiddenInput type="file" onChange={handleFileSelect1} accept="application/pdf" />
                </Button>
              </Grid>
              <Grid container item xs={12} sm={8} spacing={3} alignItems="center" justifyContent="center">
                <Grid item xs={12} sm={10}>
                  <TextField
                    fullWidth
                    label="Nombramiento del representante institucional"
                    name="form_1"
                    type="text"
                    value={form1.name ? form1.name : ''}
                    sx={{ ...theme.typography.customInput }}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button disabled={dependency.format_1 === ''} variant="outlined" onClick={() => downloadURL(dependency.format_1)}>
                    <PictureAsPdf />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid container alignItems="center" justifyContent="center">
              <Grid item xs={12} sm={4}>
                <Button disabled={dependency.format_2 !== ''} variant="outlined" endIcon={<PictureAsPdf />} component="label">
                  Cargar formato 2
                  <VisuallyHiddenInput type="file" onChange={handleFileSelect2} accept="application/pdf" />
                </Button>
              </Grid>
              <Grid container item xs={12} sm={8} spacing={3} alignItems="center" justifyContent="center">
                <Grid item xs={12} sm={10}>
                  <TextField
                    fullWidth
                    label="Acuerdo de afiliación individualizado"
                    name="form_2"
                    type="text"
                    value={form2.name ? form2.name : ''}
                    sx={{ ...theme.typography.customInput }}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button disabled={dependency.format_2 === ''} variant="outlined" onClick={() => downloadURL(dependency.format_2)}>
                    <PictureAsPdf />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {isLoading && <LinearProgress />}
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} mt={2}>
            <AnimateButton>
              <Button
                disabled={form1 === '' && form2 === ''}
                onClick={handlerSubmit}
                disableElevation
                size="large"
                variant="contained"
                color="secondary"
                startIcon={<UploadFile />}
              >
                Subir...
              </Button>
            </AnimateButton>
          </Stack>
        </MainCard>
      </Dialog>
    </>
  );
};
